import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Mic, Square, Loader2, Trash2, ChevronDown, ChevronUp, Pencil, Check, Play, Pause, ShieldAlert,
  Download, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API, dateTimeFr, downloadFile, apiError } from "@/lib/api";
import Modal from "@/components/Modal";

const MAX_SECONDS = 60 * 60;

function pickMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  return candidates.find((t) => window.MediaRecorder?.isTypeSupported(t)) || "";
}

function mimeToExt(mime) {
  if (mime.includes("mp4") || mime.includes("aac")) return "m4a";
  if (mime.includes("mpeg")) return "mp3";
  return "webm";
}

function fmtTimer(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

export default function ComptesRendus() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [editTitre, setEditTitre] = useState("");
  const [editing, setEditing] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [devisClient, setDevisClient] = useState([]);
  const audioRef = useRef(null);

  const [recorderOpen, setRecorderOpen] = useState(false);
  const [rgpdOpen, setRgpdOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [crTitre, setCrTitre] = useState("");
  const [crClient, setCrClient] = useState("");
  const [conserverAudio, setConserverAudio] = useState(true);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const load = useCallback(() => {
    const params = search ? `?q=${encodeURIComponent(search)}` : "";
    api.get(`/comptes-rendus${params}`).then((r) => setItems(r.data)).catch(() => {});
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const openId = searchParams.get("ouvrir");
    if (openId) openDetail(openId);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openDetail(id) {
    try {
      const r = await api.get(`/comptes-rendus/${id}`);
      setDetail(r.data);
      setEditTitre(r.data.titre);
      setEditing(false);
      setShowTranscription(false);
      if (r.data.client_id) {
        api.get("/devis").then((rd) => setDevisClient(rd.data.filter((d) => d.client_id === r.data.client_id))).catch(() => {});
      } else {
        setDevisClient([]);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function lierDevis(devisId) {
    try {
      await api.patch(`/comptes-rendus/${detail.id}`, { devis_id: devisId });
      setDetail({ ...detail, devis_id: devisId || null });
      toast.success(devisId ? t("cr.rattache") : t("cr.detache"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function startNewRecording() {
    if (!navigator.onLine) return toast.error(t("cr.hors_ligne"));
    if (!localStorage.getItem("mastoras_rgpd_audio_ok")) {
      setRgpdOpen(true);
      return;
    }
    setRecorderOpen(true);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMimeType();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        uploadAudio(blob, mime);
      };
      mediaRef.current = rec;
      rec.start(1000);
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stopRecording();
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error(t("cr.micro_erreur"));
    }
  }

  function stopRecording() {
    clearInterval(timerRef.current);
    setRecording(false);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  }

  async function uploadAudio(blob, mime) {
    setRecorderOpen(false);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `enregistrement.${mimeToExt(mime)}`);
      fd.append("titre", crTitre);
      fd.append("client_id", crClient);
      fd.append("duree_s", String(seconds));
      fd.append("conserver_audio", conserverAudio ? "true" : "false");
      const r = await api.post("/comptes-rendus", fd, { timeout: 120000 });
      toast.success(t("cr.envoye"));
      setCrTitre(""); setCrClient("");
      load();
      pollStatus(r.data.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  function pollStatus(id) {
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/comptes-rendus/${id}`);
        if (r.data.statut !== "en_cours") {
          clearInterval(iv);
          load();
          if (r.data.statut === "termine") {
            toast.success(t("cr.pret"));
            openDetail(id);
          } else {
            toast.error(t("cr.echec"));
          }
        }
      } catch { clearInterval(iv); }
    }, 4000);
    setTimeout(() => clearInterval(iv), 5 * 60 * 1000);
  }

  async function saveTitre() {
    try {
      await api.patch(`/comptes-rendus/${detail.id}`, { titre: editTitre });
      setDetail({ ...detail, titre: editTitre });
      setEditing(false);
      load();
      toast.success(t("cr.titre_maj"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function supprimer() {
    setBusy(true);
    try {
      await api.delete(`/comptes-rendus/${detail.id}`);
      toast.success(t("cr.supprime"));
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  function toggleAudio() {
    const el = audioRef.current;
    if (!el) return;
    if (audioPlaying) { el.pause(); setAudioPlaying(false); }
    else { el.play(); setAudioPlaying(true); }
  }

  const resume = detail?.resume || {};

  return (
    <div className="space-y-5" data-testid="page-comptes-rendus">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("cr.titre")}</h1>
          <p className="text-stone-500 text-sm mt-0.5">{t("cr.sous_titre")}</p>
        </div>
        <button onClick={startNewRecording} disabled={busy}
                className="btn-accent !h-14 !px-6 !text-base" data-testid="btn-nouvel-enregistrement">
          {busy ? <Loader2 className="animate-spin" size={22} /> : <Mic size={22} />} {t("cr.nouvel")}
        </button>
      </div>

      <input className="input-field" placeholder={t("cr.recherche")} value={search}
             onChange={(e) => setSearch(e.target.value)} data-testid="input-recherche-cr" />

      <div className="space-y-2" data-testid="liste-comptes-rendus">
        {items.length === 0 && (
          <div className="panel text-center py-10">
            <Mic size={32} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500 text-sm">{t("cr.vide")}</p>
          </div>
        )}
        {items.map((cr) => (
          <button key={cr.id} onClick={() => openDetail(cr.id)}
                  className="panel w-full text-left flex items-center gap-3 hover:border-[#D9A461]/50 transition-colors"
                  data-testid={`item-cr-${cr.id}`}>
            <div className="w-11 h-11 rounded-full bg-[#12100D] text-[#D9A461] flex items-center justify-center shrink-0">
              <Mic size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{cr.titre}</div>
              <div className="text-xs text-stone-500">
                {dateTimeFr(cr.cree_le)}{cr.client_nom ? ` · ${cr.client_nom}` : ""}
                {cr.duree_s ? ` · ${fmtTimer(cr.duree_s)}` : ""}
              </div>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${
              cr.statut === "termine" ? "bg-[#E4F2E7] text-[#276440] border-[#B8E2C2]"
              : cr.statut === "erreur" ? "bg-[#FBE4E0] text-[#A32E20] border-[#F2C2BA]"
              : "bg-[#FCEFD9] text-[#8A5A0F] border-[#F6D9A9]"}`}
                  data-testid={`statut-cr-${cr.id}`}>
              {cr.statut === "termine" ? t("cr.statut_pret") : cr.statut === "erreur" ? t("cr.statut_erreur") : t("cr.statut_cours")}
            </span>
          </button>
        ))}
      </div>

      <Modal open={rgpdOpen} onClose={() => setRgpdOpen(false)} title={t("cr.rgpd_titre")} testid="modal-rgpd-audio">
        <div className="flex items-start gap-3 mb-4">
          <ShieldAlert size={24} className="text-[#8A5A0F] shrink-0 mt-0.5" />
          <p className="text-sm text-stone-600">
            {t("cr.rgpd_texte")}{" "}
            <Link to="/confidentialite" className="text-[#B5762A] underline"
                  data-testid="lien-rgpd-confidentialite">{t("cr.rgpd_lien")}</Link>.
          </p>
        </div>
        <button onClick={() => { localStorage.setItem("mastoras_rgpd_audio_ok", "1"); setRgpdOpen(false); setRecorderOpen(true); }}
                className="btn-accent w-full" data-testid="btn-accepter-rgpd-audio">
          {t("cr.rgpd_ok")}
        </button>
      </Modal>

      <Modal open={recorderOpen} onClose={() => { if (!recording) setRecorderOpen(false); }}
             title={t("cr.modal_titre")} testid="modal-enregistreur">
        {!recording ? (
          <div className="space-y-4">
            <input className="input-field" placeholder={t("cr.titre_placeholder")} value={crTitre}
                   onChange={(e) => setCrTitre(e.target.value)} data-testid="input-titre-cr" />
            <select value={crClient} onChange={(e) => setCrClient(e.target.value)}
                    className="input-field" data-testid="select-client-cr">
              <option value="">{t("cr.sans_client")}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <label className="flex items-center gap-3 text-sm text-stone-700 min-h-[44px]">
              <input type="checkbox" checked={conserverAudio}
                     onChange={(e) => setConserverAudio(e.target.checked)}
                     className="w-5 h-5 accent-[#B5762A]" data-testid="checkbox-conserver-audio" />
              {t("cr.conserver")}
            </label>
            <button onClick={startRecording} className="btn-accent w-full !h-16 !text-lg"
                    data-testid="btn-demarrer-enregistrement">
              <Mic size={26} /> {t("cr.demarrer")}
            </button>
            <p className="text-xs text-stone-500 text-center">{t("cr.duree_max")}</p>
          </div>
        ) : (
          <div className="text-center space-y-5 py-4">
            <div className="flex items-center justify-center gap-3">
              <span className="relative flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A32E20] opacity-60"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-[#A32E20]"
                      data-testid="pastille-enregistrement"></span>
              </span>
              <span className="font-serif font-bold text-3xl tabular-nums" data-testid="minuteur-enregistrement">
                {fmtTimer(seconds)}
              </span>
            </div>
            <p className="text-sm text-stone-500">{t("cr.en_cours")}</p>
            <button onClick={stopRecording}
                    className="btn-accent !bg-[#A32E20] w-full !h-16 !text-lg"
                    data-testid="btn-arreter-enregistrement">
              <Square size={24} /> {t("cr.arreter")}
            </button>
          </div>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => { setDetail(null); setAudioPlaying(false); }}
             title="" testid="modal-detail-cr">
        {detail && (
          <div className="space-y-4">
            <div>
              {editing ? (
                <div className="flex gap-2">
                  <input className="input-field" value={editTitre}
                         onChange={(e) => setEditTitre(e.target.value)} data-testid="input-edit-titre-cr" />
                  <button onClick={saveTitre} className="btn-accent !px-4 shrink-0" data-testid="btn-save-titre-cr">
                    <Check size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="font-serif font-bold text-xl flex-1 min-w-0" data-testid="titre-cr">{detail.titre}</h2>
                  <button onClick={() => setEditing(true)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#E6DFD1] text-stone-500"
                          data-testid="btn-edit-titre-cr"><Pencil size={15} /></button>
                </div>
              )}
              <div className="text-xs text-stone-500 mt-1">
                {dateTimeFr(detail.cree_le)}{detail.client_nom ? ` · ${detail.client_nom}` : ""}
                {detail.duree_s ? ` · ${fmtTimer(detail.duree_s)}` : ""}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="cr-actions">
              <button onClick={() => downloadFile(`/comptes-rendus/${detail.id}/pdf`,
                        `${(detail.titre || "compte-rendu").replace(/[^A-Za-z0-9_-]+/g, "-")}.pdf`)}
                      className="btn-secondary !h-11" data-testid="btn-telecharger-cr-pdf">
                <Download size={16} /> {t("cr.telecharger_pdf")}
              </button>
              {detail.client_id && (
                <select className="input-field !h-11 !w-auto text-sm" value={detail.devis_id || ""}
                        onChange={(e) => lierDevis(e.target.value)} data-testid="select-devis-cr">
                  <option value="">{t("cr.rattacher")}</option>
                  {devisClient.map((d) => (
                    <option key={d.id} value={d.id}>{d.numero} ({t(`statuts.${d.statut}`, d.statut)})</option>
                  ))}
                </select>
              )}
              {detail.devis_id && (
                <Link to={`/devis/${detail.devis_id}`} className="btn-secondary !h-11"
                      data-testid="lien-devis-depuis-cr">
                  <FileText size={16} /> {t("cr.voir_devis")}
                </Link>
              )}
            </div>

            {detail.statut === "en_cours" && (
              <div className="flex items-center gap-2 text-sm text-[#8A5A0F]" data-testid="cr-en-cours">
                <Loader2 className="animate-spin" size={16} /> {t("cr.gen_en_cours")}
              </div>
            )}
            {detail.statut === "erreur" && (
              <p className="text-sm text-[#A32E20]" data-testid="cr-erreur">{t("cr.gen_erreur")}</p>
            )}

            {detail.statut === "termine" && (
              <>
                {detail.has_audio && (
                  <div className="flex items-center gap-3">
                    <button onClick={toggleAudio}
                            className="w-12 h-12 rounded-full bg-[#12100D] text-[#D9A461] flex items-center justify-center shrink-0"
                            data-testid="btn-play-audio">
                      {audioPlaying ? <Pause size={20} /> : <Play size={20} />}
                    </button>
                    <span className="text-sm text-stone-600">{t("cr.reecouter")}</span>
                    <audio ref={audioRef} src={`${API}/comptes-rendus/${detail.id}/audio`}
                           onEnded={() => setAudioPlaying(false)} className="hidden" data-testid="audio-player-cr" />
                  </div>
                )}

                <div data-testid="resume-cr">
                  {[
                    ["points_cles", t("cr.points_cles")],
                    ["decisions", t("cr.decisions")],
                    ["actions", t("cr.actions")],
                  ].map(([key, label]) => (
                    <div key={key} className="mb-4">
                      <h3 className="font-serif font-semibold text-base mb-2">{label}</h3>
                      {(resume[key] || []).length === 0 ? (
                        <p className="text-sm text-stone-400 italic">{t("cr.rien")}</p>
                      ) : (
                        <ul className="list-disc pl-5 space-y-1 text-sm text-stone-700">
                          {resume[key].map((pt, i) => <li key={i}>{pt}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                <button onClick={() => setShowTranscription(!showTranscription)}
                        className="flex items-center gap-2 text-sm font-medium text-[#B5762A] min-h-[44px]"
                        data-testid="btn-toggle-transcription">
                  {showTranscription ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {t("cr.transcription")}
                </button>
                {showTranscription && (
                  <div className="bg-[#FDFBF7] border border-[#E6DFD1] rounded-lg p-4 text-sm text-stone-700 whitespace-pre-wrap max-h-72 overflow-y-auto"
                       data-testid="transcription-cr">
                    {detail.transcription || t("cr.transcription_vide")}
                  </div>
                )}
              </>
            )}

            <button onClick={supprimer} disabled={busy}
                    className="btn-secondary w-full !text-[#A32E20]" data-testid="btn-supprimer-cr">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />} {t("cr.supprimer")}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
